// The Fusion Chamber (§31 A8): where two pieces may be fused, and who pays.
//
// The artifact is a Fusion Chamber (kind 19) and the Signet of Union on a
// Monarch. The Chamber **acts as an Armory** whether or not the Signet is
// borne — that is the building's function (`actsAsArmory`), as the Sacred
// Well generates without its Scepter. **Fusing is the artifact's**: the
// `fusion` power, Chamber ∧ Signet (`hasPower`).
//
// The design's site ("How to fuse pieces"): the parts adjacent to each other,
// and either a forge-capable piece adjacent, or within 2 of an Armory or the
// fusing structure, "with the material cost available from the one of those
// that applies" — so a site is a list of payers, P3's shape: forgers
// touching every part (themselves not parts), then own Armories and Chambers
// within `POWER.armoryReach` of every part, each by square.
//
// **The fuse itself (§31 P4)**: `fuse {parts, into}` names the bodies and a
// fuse row (`PieceVariant.fusedFrom`). One source pays the row's bill
// (`upgradeBill`, so the Artisan discount applies) and `RULES.upgradeFuel`, by
// P3's rule over `fuseSources`. The **anchor** — the row's first part — stays:
// square, id, wounds, banks, gear; it takes the row's `base` and its armor
// (`reframePlating`, from what it wore). The rest are **consumed**: no corpse,
// no heap; what they held pours into the anchor's inbox, the overflow set down
// where they stood. Free like `upgrade` (no move, no tick), turn-bound,
// run-only (the power is an artifact), king-safe.

import { type Color, fileOf, makePiece, pieceColor, pieceType, rankOf, type Side } from "../board";
import type { GameState } from "../commands";
import { isFoundryAt } from "../industry/machines";
import {
  dropOnGround,
  pushUnit,
  stripHoldings,
  structureAt,
  structureSquares,
  unitAt,
  unitCount
} from "../industry/state";
import { actsAsArmory, neighbours, touches } from "../industry/structures";
import { payUpgrade, shortRow, sourceFuel, upgradeBill } from "../industry/upgrades";
import { pieceAt, reframePlating, removePiece, setPiece, setVariant, variantAt } from "../position";
import { PIECE_PLATING, pieceVariant, PIECE_VARIANTS, POWER, RULES } from "../rules";
import { settleAction } from "../clock";
import { cloneState, IllegalCommandError, requireKingSafe, requireOwnTurn } from "../reduce";
import { hasPower } from "./powers";

/** Why these squares may not fuse here, or null. */
export type FuseSiteError =
  // No Fusion Chamber ∧ Signet of Union of yours.
  | "no-fusion"
  // Fewer than two parts, a repeat, or a part that is not your piece.
  | "not-your-piece"
  // The parts do not stand together.
  | "not-adjacent"
  // No forger touches every part and no Armory or Chamber reaches them all.
  | "out-of-fusion-reach";

/** What each refusal says. */
export const FUSE_REFUSAL: Record<FuseSiteError, string> = {
  "no-fusion": "fusing needs a Fusion Chamber and the Signet of Union",
  "not-your-piece": "fuse two or more pieces of yours",
  "not-adjacent": "the pieces to fuse must stand side by side",
  "out-of-fusion-reach": "fuse beside a forger, or within 2 of an Armory or the Fusion Chamber"
};

const chebyshev = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/** Do the parts form one group, each touching another (eight-way)? */
function standTogether(parts: readonly number[]): boolean {
  const reached = new Set([parts[0]]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const sq of parts) {
      if (reached.has(sq)) continue;
      if ([...reached].some((at) => touches(at, sq))) {
        reached.add(sq);
        grew = true;
      }
    }
  }
  return reached.size === parts.length;
}

/**
 * Who may pay a fuse of `parts`, in the order asked: `owner`'s forge-capable
 * pieces touching every part (not themselves parts), then `owner`'s Armories
 * and Fusion Chambers within `POWER.armoryReach` of every part, each by square.
 */
export function fuseSources(state: GameState, owner: Side, parts: readonly number[]): number[] {
  const { position, industry } = state;
  const forgers = new Set<number>();
  for (const sq of neighbours(parts[0])) {
    if (parts.includes(sq)) continue;
    const piece = pieceAt(position, sq);
    if (piece === 0 || pieceColor(piece) !== owner || !isFoundryAt(position, industry, sq)) continue;
    if (parts.every((part) => touches(part, sq))) forgers.add(sq);
  }
  const armories = structureSquares(industry).filter((sq) => {
    const one = structureAt(industry, sq)!;
    return (
      actsAsArmory(one.kind) &&
      one.owner === owner &&
      parts.every((part) => chebyshev(sq, part) <= POWER.armoryReach)
    );
  });
  return [...[...forgers].sort((a, b) => a - b), ...armories];
}

/** May `owner` fuse the pieces on `parts` where they stand? */
export function fuseSiteError(state: GameState, owner: Side, parts: readonly number[]): FuseSiteError | null {
  if (!hasPower(state.industry, owner, "fusion")) return "no-fusion";
  if (parts.length < 2 || new Set(parts).size !== parts.length) return "not-your-piece";
  for (const sq of parts) {
    const piece = pieceAt(state.position, sq);
    if (piece === 0 || pieceColor(piece) !== owner) return "not-your-piece";
  }
  if (!standTogether(parts)) return "not-adjacent";
  return fuseSources(state, owner, parts).length > 0 ? null : "out-of-fusion-reach";
}

/** Why this fuse may not be made, or null: the site's reasons, then the recipe's and the price's. */
export type FuseError =
  | FuseSiteError
  // `into` is no fuse row.
  | "not-a-fuse"
  // The parts are not the row's recipe.
  | "wrong-parts"
  // No source covers the bill.
  | "too-poor"
  // One covers the bill, none the fuel too.
  | "no-fuel";

/** What every refusal says — the site's from A8, the rest P4's. */
export const FUSE_ERROR_REFUSAL: Record<FuseError, string> = {
  ...FUSE_REFUSAL,
  "not-a-fuse": "that is no fuse",
  "wrong-parts": "those pieces do not fuse into that",
  "too-poor": "no forger beside them or Armory in reach can pay the fuse",
  "no-fuel": `the fuse needs ${RULES.upgradeFuel} fuel at its source`
};

/** The parts row `id` is fused from, or null for a row that is no fuse. */
const recipeOf = (id: number) => PIECE_VARIANTS[id]?.fusedFrom ?? null;

/**
 * The parts in the row's order — the anchor first — or null when the squares
 * are not exactly its recipe. A recipe's parts are distinct (rank, row)
 * pairs, so a match is a permutation.
 */
export function fuseOrder(state: GameState, parts: readonly number[], into: number): number[] | null {
  const recipe = recipeOf(into);
  if (!recipe || recipe.length !== parts.length) return null;
  const left = [...parts];
  const ordered: number[] = [];
  for (const part of recipe) {
    const i = left.findIndex((sq) => {
      const piece = pieceAt(state.position, sq);
      return piece !== 0 && pieceType(piece) === part.type && variantAt(state.position, sq) === part.variant;
    });
    if (i < 0) return null;
    ordered.push(left[i]);
    left.splice(i, 1);
  }
  return ordered;
}

/** Who pays fuse `into` of `parts`, or why none can: P3's `pickUpgradeSource` over `fuseSources`. */
export function pickFuseSource(
  state: GameState,
  owner: Color,
  parts: readonly number[],
  into: number
): { source: number } | { why: "too-poor" | "no-fuel" } {
  const bill = upgradeBill(state.industry, owner, into);
  let rich = false;
  for (const source of fuseSources(state, owner, parts)) {
    if (shortRow(state.industry, source, owner, bill)) continue;
    rich = true;
    if (sourceFuel(state.industry, source, owner) >= RULES.upgradeFuel) return { source };
  }
  return { why: rich ? "no-fuel" : "too-poor" };
}

/** May `owner` fuse `parts` into row `into` now (the clock aside)? */
export function fuseError(
  state: GameState,
  owner: Color,
  parts: readonly number[],
  into: number
): FuseError | null {
  if (!recipeOf(into)) return "not-a-fuse";
  const site = fuseSiteError(state, owner, parts);
  if (site === "no-fusion" || site === "not-your-piece") return site;
  if (!fuseOrder(state, parts, into)) return "wrong-parts";
  if (site) return site;
  const pick = pickFuseSource(state, owner, parts, into);
  return "why" in pick ? pick.why : null;
}

/** One fuse the piece on a square could join, for the session's list. */
export interface FuseChoice {
  into: number;
  parts: number[];
  why: FuseError | null;
}

/**
 * Every fuse `owner` could make with the piece on `at` as a part: per row, the
 * first group found — each other part the lowest square of the right (rank,
 * row) touching the group so far — and why it is refused. A row whose other
 * parts do not stand by is omitted.
 */
export function fuseChoices(state: GameState, owner: Color, at: number): FuseChoice[] {
  const { position } = state;
  const piece = pieceAt(position, at);
  if (!piece || pieceColor(piece) !== owner) return [];
  const out: FuseChoice[] = [];
  for (let into = 0; into < PIECE_VARIANTS.length; into++) {
    const recipe = recipeOf(into);
    if (!recipe) continue;
    const mine = recipe.findIndex(
      (part) => part.type === pieceType(piece) && part.variant === variantAt(position, at)
    );
    if (mine < 0) continue;
    const parts = [at];
    for (let i = 0; i < recipe.length; i++) {
      if (i === mine) continue;
      const part = recipe[i];
      const near = [...new Set(parts.flatMap((sq) => neighbours(sq)))]
        .filter((sq) => !parts.includes(sq))
        .filter((sq) => {
          const other = pieceAt(position, sq);
          return (
            other !== 0 &&
            pieceColor(other) === owner &&
            pieceType(other) === part.type &&
            variantAt(position, sq) === part.variant
          );
        })
        .sort((a, b) => a - b);
      if (near.length === 0) break;
      parts.push(near[0]);
    }
    if (parts.length !== recipe.length) continue;
    out.push({ into, parts, why: fuseError(state, owner, parts, into) });
  }
  return out;
}

/** `fuse`: make row `into` of `parts`. See the head of this file. */
export function applyFuse(state: GameState, command: { parts: readonly number[]; into: number }): GameState {
  const owner = requireOwnTurn(state);
  const why = fuseError(state, owner, command.parts, command.into);
  if (why) throw new IllegalCommandError(`cannot fuse: ${FUSE_ERROR_REFUSAL[why]}`);
  const [anchor, ...consumed] = fuseOrder(state, command.parts, command.into)!;
  const pick = pickFuseSource(state, owner, command.parts, command.into);
  if ("why" in pick) throw new IllegalCommandError(`cannot fuse: ${FUSE_ERROR_REFUSAL[pick.why]}`);

  const next = cloneState(state);
  const { position, industry } = next;
  payUpgrade(industry, pick.source, owner, upgradeBill(industry, owner, command.into));

  // The consumed bodies: no corpse, no heap. What they held pours into the
  // anchor's inbox; what will not fit is set down where they stood.
  for (const sq of consumed) {
    const held: number[] = [];
    for (const store of ["in", "out"] as const) {
      for (let i = 0; i < unitCount(industry, sq, store); i++) held.push(unitAt(industry, sq, i, store));
    }
    held.push(...(industry.cooking.get(sq) ?? []));
    stripHoldings(industry, sq);
    removePiece(position, sq);
    for (const unit of held) if (!pushUnit(industry, anchor, unit, "in")) dropOnGround(industry, sq, unit);
  }

  // The anchor: the row's rank, the row, and the row's whole armor in place
  // of what it wore (its old row's, else its rank's) — `upgrade`'s K1 rule.
  const was = pieceAt(position, anchor);
  const row = pieceVariant(command.into);
  const wore = pieceVariant(variantAt(position, anchor)).plating ?? PIECE_PLATING[pieceType(was)];
  if (pieceType(was) !== row.base) setPiece(position, anchor, makePiece(owner, row.base));
  setVariant(position, anchor, command.into);
  reframePlating(position, anchor, (row.plating ?? PIECE_PLATING[row.base]) - wore);

  // Bodies gone can open a line onto your own king.
  requireKingSafe(position, owner, "fuse");
  // Free, like `upgrade` (§31 T4): factory work.
  settleAction(next, owner);
  return next;
}
